import React, { useEffect, useMemo } from 'react';
import { ListPageHeader } from '@presentation/components/ListPageHeader';
import { ListHeader } from '@presentation/components/ListHeader';
import { ListContent } from '@presentation/layouts/styles';
import { SORT_OPTIONS } from '@presentation/pages/Category/constants';
import type { CollectionsService } from '@domain/usecases/collections/CollectionsService';
import { useCategoryContents } from '@presentation/pages/Category/hooks/useCategoryContents';
import type { PuzzleSortField } from '@domain/usecases/puzzles/PuzzleSortField';
import { ArtworkInfiniteScrollList } from '@presentation/components/ArtworkInfiniteScrollList/ArtworkInfiniteScrollList';
import { useSmartBanner } from '@presentation/context/SmartBannerContextProvider';
import type { CollectionModel } from '@domain/models/CollectionModel';
import { usePageContext } from 'vike-react/usePageContext';
import { JsonLd } from '@presentation/seo/JsonLd';
import { buildBreadcrumb, buildCategoryGraph } from '@presentation/seo/schema';
import { Container, Typography } from '@mui/material';

const CategoryPage = ({
	collectionsService,
	category,
	firstPage,
	intro,
}: {
	collectionsService: CollectionsService;
	category: CollectionModel;
	firstPage: any;
	intro?: string;
}) => {
	const { isOpen, generateLink } = useSmartBanner();

	const { routeParams } = usePageContext();
	const { contentIdWithNiceName } = routeParams;

	const id = useMemo(() => contentIdWithNiceName?.split('-')[0] || '', [contentIdWithNiceName]);

	const { data, fetchContent, hasMore, handleSort, sort, count } = useCategoryContents(
		category?.id,
		firstPage,
		collectionsService
	);

	useEffect(() => {
		if (id) {
			generateLink('CATEGORY', id);
		}
	}, [id, generateLink]);

	return (
		<>
			<JsonLd data={buildCategoryGraph(category, `/category/${category.id}`)} />
			<JsonLd
				data={buildBreadcrumb([
					{ name: 'Home', url: '/' },
					{ name: 'Categories', url: '/categories' },
					{ name: category.name },
				])}
			/>
			<ListPageHeader
				breadcrumbs={[
					{
						label: 'screens.home.title',
						url: '/',
					},
					{
						label: 'screens.categories.title',
						url: '/categories',
					},
					{
						label: category.name,
					},
				]}
				title={`${category.name} jigsaw puzzles`}
				count={count}
			/>
			{intro && (
				<Container maxWidth='xl'>
					<Typography
						component='h2'
						sx={{
							px: 2,
							mt: 2,
							mb: 2,
							fontSize: { xs: 16, md: 17 },
							fontWeight: 400,
							lineHeight: 1.7,
							color: 'text.primary',
							maxWidth: '68ch',
							textWrap: 'pretty',
						}}
					>
						{intro}
					</Typography>
				</Container>
			)}
			<ListContent isSmartBannerOpen={isOpen} maxWidth='xl'>
				<ListHeader
					sortOptions={SORT_OPTIONS}
					sortBy={`${sort.field}|${sort.direction}`}
					onSortChange={(orderBy, orderDirection) => handleSort(orderBy as PuzzleSortField, orderDirection)}
				/>
				<ArtworkInfiniteScrollList data={data} hasMore={hasMore} loadMore={() => fetchContent()} />
			</ListContent>
		</>
	);
};

export default CategoryPage;
